import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, Inbox, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data.items);
    setUnread(data.unread);
  };

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All marked as read");
    await load();
  };

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    await load();
  };

  const requestPush = async () => {
    if (!("Notification" in window)) {
      toast.error("This browser does not support notifications.");
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      toast.error("Notification permission denied");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      // Best-effort: save the subscription if push is configured later.
      try {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await api.post("/notifications/push-subscription", sub.toJSON());
      } catch (err) { /* push subscription is best-effort */ }
      new Notification("Notifications enabled", { body: "We'll alert you on new activity." });
      toast.success("Notifications enabled in your browser");
    } catch (e) {
      toast.error("Could not enable push notifications");
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tag"><span className="w-1.5 h-1.5 bg-[#C8102E]" /> Activity</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Notifications</h1>
          <p className="text-zinc-600 text-sm mt-1">{unread} unread of {items.length} total.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={requestPush} data-testid="enable-push-btn" className="inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-100 px-4 py-2 text-sm font-semibold transition-colors">
            <Bell className="w-4 h-4" /> Enable push
          </button>
          <button onClick={markAll} data-testid="mark-all-read-btn" className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold transition-colors">
            <Check className="w-4 h-4" /> Mark all read
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        {items.length === 0 && (
          <div className="p-12 text-center text-zinc-500">
            <Inbox className="w-8 h-8 mx-auto text-zinc-400" />
            <div className="mt-3 text-sm">You're all caught up.</div>
          </div>
        )}
        {items.map((n) => (
          <div key={n.notification_id} className="border-b border-zinc-100 p-4 flex items-start gap-3" data-testid={`notification-${n.notification_id}`}>
            <div className={`w-2 h-2 mt-2 ${n.read ? "bg-zinc-300" : "bg-[#C8102E]"}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="font-display font-black text-sm tracking-tight">{n.title}</div>
                <span className="text-[10px] text-zinc-500 font-mono">
                  {new Date(n.created_at).toLocaleString()}
                </span>
              </div>
              {n.body && <div className="text-sm text-zinc-600 mt-1">{n.body}</div>}
              {n.link && (
                <Link to={n.link} className="text-xs text-[#C8102E] uppercase tracking-widest font-semibold mt-1 inline-block hover:underline">
                  View →
                </Link>
              )}
            </div>
            {!n.read && (
              <button onClick={() => markOne(n.notification_id)} data-testid={`notification-read-${n.notification_id}`} className="text-xs text-zinc-500 hover:text-[#C8102E] uppercase tracking-widest font-semibold">
                Mark read
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
